'use client'

import { Button } from '@/components/ui/button'
import { useSectionRefs } from '@/context/SectionRefsContext'
import { motion } from 'framer-motion'
import { Mail, MessageCircle, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Textarea } from '../ui/textArea'
import Input from '../ui/input'
import { LandingPageData } from '@/redux/api/landingPageApi'
import { useCreateContactInquiryMutation } from '@/redux/api/contactInquiryApi'
import { useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import Label from '../ui/label'

export default function LandingContact({ data }: { data?: LandingPageData['contact'] }) {
  const { t } = useTranslation()
  const { registerRef } = useSectionRefs()
  const [createInquiry, { isLoading }] = useCreateContactInquiryMutation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error(t('please_fill_all_fields'))
      return
    }
    try {
      const res = await createInquiry(formData).unwrap()
      toast.success(res.message || t('message_sent_successfully'))
      setFormData({ name: '', email: '', subject: '', message: '' })
    } catch (error) {
      const apiError = error as any
      toast.error(apiError?.data?.message || t('something_went_wrong'))
    }
  }

  const sectionBadge = data?.section_badge || t('get_in_touch')
  const sectionTitle = data?.heading || t('have_questions_we_have')
  const sectionDescription = data?.subheading || t('contact_description')

  return (
    <section
      id="contact"
      ref={(el) => registerRef('contact', el)}
      className="relative py-24 md:py-30 overflow-hidden"
    >

      {/* Background Decorative Glows */}
      <div className="absolute top-0 left-0 -translate-x-1/2 w-[50%] h-[500px] bg-primary/15 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute top-0 left-0 -translate-x-1/2 w-[40%] h-[300px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="absolute bottom-0 right-0 translate-x-1/2 w-[70%] h-[500px] bg-primary/15 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 translate-x-1/2 w-[50%] h-[300px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-16">
          <div className="flex-1">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-widest mb-4"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {sectionBadge}
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-4xl md:text-5xl font-bold text-white mb-3 leading-tight"
            >
              {sectionTitle}{' '}
              {sectionTitle === t('have_questions_we_have') && (
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto] animate-gradient">{t('answers')}</span>
              )}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="text-lg text-white/60 mb-10"
            >
              {sectionDescription}
            </motion.p>

            <div className="space-y-8 flex flex-col sm:flex-row gap-6">
              <div className="flex items-center gap-4 mb-0">
                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold">{t('email_us')}</h4>
                  <p className="text-white/40">{data?.email || 'hello@example.ai'}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <MessageCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold">{data?.live_chat_label || t('live_chat')}</h4>
                  <p className="text-white/40">{t('available_24_7')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="p-8 md:p-10 rounded-[2.5rem] bg-white/5 border border-white/10 backdrop-blur-xl"
            >
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 flex flex-col">
                    <Label className="text-sm font-bold text-white/60 ml-1 flex-1 mb-1">{t('your_name')}</Label>
                    <Input
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-emerald-400/50 transition-all"
                    />
                  </div>
                  <div className="space-y-2 flex flex-col">
                    <Label className="text-sm font-bold text-white/60 ml-1">{t('email_address')}</Label>
                    <Input
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-emerald-400/50 transition-all"
                    />
                  </div>
                </div>
                <div className="space-y-2 flex flex-col">
                  <Label className="text-sm font-bold text-white/60 ml-1">{t('subject')}</Label>
                  <Input
                    placeholder="How can we help?"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-emerald-400/50 transition-all"
                  />
                </div>
                <div className="space-y-2 flex flex-col">
                  <Label className="text-sm font-bold text-white/60 ml-1">{t('message')}</Label>
                  <Textarea
                    placeholder="Tell us more about your inquiry..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="min-h-[150px] rounded-2xl bg-white/5 border-white/10 text-white placeholder:text-white/20 focus:border-emerald-400/50 transition-all resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-14 rounded-xl sidebar-active-item text-white font-bold text-lg flex items-center justify-center gap-2 transition-all "
                >
                  {isLoading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      {t('send_message')}
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
